/* PPF-Kits: Judge.me "Cards carousel" on phones (< 750px)
   Judge.me moves its cards with a transform and a flick-only swipe and shows one centred card with no position.
   On phones this controller turns the card wrapper into a native scroll-snap strip (cards on the page gutter,
   the next card peeks in) and adds a position indicator below it (counter "03 / 20" + progress thumb that
   follows the finger). Desktop/tablet (>= 750px) keeps Judge.me's own carousel, nothing is changed there.
   - Judge.me stays the source of truth: its autoplay (and our buttons for mouse users) call
     jdgmNextCard/jdgmPreviousCard, we scroll to its ".active" card. After a swipe we write the card back
     (jdgmCarouselMode[id].index + .active) and restart its timer, so autoplay continues from what the visitor sees.
   - Autoplay is held while a finger is on the strip (until the swipe has come to rest) and while keyboard focus
     is inside the widget; a Judge.me move during the visitor's own scroll is overruled by the visitor's card.
   - Touches on the strip still reach page-level touchstart listeners; their touchmove/touchend stop at the strip
     (Judge.me's flick handler on .jdgm-content would otherwise cancel the native scroll or jump a card).
     At either end of the strip an outward swipe is cancelled, so it cannot turn into the browser's back gesture.
   - Screen readers: the indicator comes before the cards in the DOM (shown below them), its position text is a
     polite live region that only changes on the visitor's own moves (swipe, buttons), never on autoplay.
   - prefers-reduced-motion: no smooth scrolling and no autoplay moves on phones.
   - Fail-safe: every rule in assets/ppf-reviews-carousel.css needs the class .ppf-rc, which is only set when the
     stylesheet is loaded and Judge.me's markup and state are exactly as expected (checked again whenever Judge.me
     rebuilds the cards); on a mismatch or an error the class is removed and Judge.me's carousel is back as it was.
     If Judge.me replaces its content block, the controller starts over on the new markup (at most 3 times).
   Config: window.PPF_RC_CFG (snippets/ppf-reviews-carousel.liquid). */
(function () {
  'use strict';
  if (window.__ppfRcInit) return;
  window.__ppfRcInit = true;

  var CFG = window.PPF_RC_CFG || {};
  var T = CFG.i18n || {};
  var PHONE = window.matchMedia('(max-width: 749px)');
  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
  var ON = 'ppf-rc';
  var HAS_SCROLLEND = 'onscrollend' in window;
  var THUMB_MIN = 20; // same size as the white bar under the section headings
  var MAX_RENEW = 3;
  var list = [];
  var ICON = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function kid(el, cls) {
    for (var i = 0; i < el.children.length; i++) if (el.children[i].classList.contains(cls)) return el.children[i];
    return null;
  }
  function pad2(n) { return n < 10 ? '0' + n : String(n); }
  function esc(t) { return String(t).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;'); }
  function say(tpl, i, n) { return String(tpl || 'Review {i} of {n}').replace('{i}', i).replace('{n}', n); }
  function clamp(s, i) { return Math.min(s.n - 1, Math.max(0, i)); }

  function cssReady(root) {
    return getComputedStyle(root).getPropertyValue('--ppf-rc-css').trim() === '1';
  }

  // Judge.me cards carousel (app version 753): root > .jdgm-content > .jdgm-cards-wrapper > .jdgm-videos-container > cards
  function read(root) {
    var content = kid(root, 'jdgm-content');
    var wrapper = content && kid(content, 'jdgm-cards-wrapper');
    var track = wrapper && kid(wrapper, 'jdgm-videos-container');
    if (!track) return null;
    // Judge.me's stylesheet can arrive after its cards (product pages): wait until its flex row is applied
    if (getComputedStyle(track).display !== 'flex') return null;
    var id = root.getAttribute('data-jdgm-block-id');
    var state = id && window.jdgmCarouselMode && window.jdgmCarouselMode[id];
    if (!state || typeof state.index !== 'number' || typeof state.revCount !== 'number') return null;
    var cards = [];
    for (var i = 0; i < track.children.length; i++) {
      var c = track.children[i];
      if (!c.classList.contains('jdgm-media-card')) return null;
      if (c.hasAttribute('data-clone-type')) continue; // Judge.me's loop clones: hidden on phones
      if (c.getAttribute('data-card-index') !== String(cards.length)) return null;
      cards.push(c);
    }
    if (cards.length < 2 || cards.length !== state.revCount) return null;
    return { id: id, state: state, content: content, wrapper: wrapper, track: track, cards: cards };
  }

  function tryInit(root) {
    if (root.__ppfRc) return true;
    if (root.classList.contains('jdgm-hidden') || !cssReady(root)) return false;
    if (root.closest('#jdgm-cart-drawer-widget, #jdgm-cart-drawer-source')) return true;
    var s = read(root);
    if (!s) return false;
    s.root = root;
    s.n = s.cards.length;
    s.cur = -1;
    s.goal = s.state.index;
    s.g = -1;
    s.touching = false;
    s.moving = false;
    s.auto = false;
    s.byUser = false;
    s.held = false;
    s.focus = false;
    s.wasPhone = false;
    s.raf = 0;
    s.timer = 0;
    root.__ppfRc = s;
    try {
      build(s);
      bind(s);
      list.push(s);
      mode(s);
    } catch (err) {
      teardown(s);
      if (window.console) console.warn('[ppf-rc]', err);
    }
    return true;
  }

  function build(s) {
    var ind = document.createElement('div');
    ind.className = 'ppf-rc-ind';
    ind.innerHTML =
      '<span class="ppf-rc-sr" aria-live="polite" aria-atomic="true">' + esc(say(T.pos, clamp(s, s.state.index) + 1, s.n)) + '</span>' +
      '<span class="ppf-rc-count" aria-hidden="true"><b class="ppf-rc-now">01</b><span class="ppf-rc-of">&thinsp;/&thinsp;' + pad2(s.n) + '</span></span>' +
      '<span class="ppf-rc-bar" aria-hidden="true"><i class="ppf-rc-thumb"></i></span>' +
      '<span class="ppf-rc-btns">' +
        '<button type="button" class="ppf-rc-btn" data-dir="-1" aria-label="' + esc(T.prev || 'Previous review') + '">' + ICON + '</button>' +
        '<button type="button" class="ppf-rc-btn ppf-rc-btn--next" data-dir="1" aria-label="' + esc(T.next || 'Next review') + '">' + ICON + '</button>' +
      '</span>';
    s.ind = ind;
    s.now = ind.querySelector('.ppf-rc-now');
    s.bar = ind.querySelector('.ppf-rc-bar');
    s.thumb = ind.querySelector('.ppf-rc-thumb');
    s.sr = ind.querySelector('.ppf-rc-sr');
    // before the cards for keyboard and screen-reader users; the stylesheet shows it below them
    s.root.insertBefore(ind, s.content);
    s.root.classList.add(ON);
  }

  function bind(s) {
    var w = s.wrapper;
    s.on = {
      touchstart: function (e) {
        if (!PHONE.matches) return;
        var t = e.touches[0];
        s.touching = true;
        s.auto = false;
        s.lock = 0;
        s.x0 = s.lx = t ? t.clientX : 0;
        s.y0 = t ? t.clientY : 0;
        hold(s);
      },
      touchmove: function (e) {
        if (!PHONE.matches || !s.touching) return;
        e.stopPropagation(); // Judge.me's flick handler on .jdgm-content cancels horizontal moves
        var t = e.touches[0];
        if (!t || e.touches.length > 1) { s.lock = 2; return; }
        var dx = t.clientX - s.x0, dy = t.clientY - s.y0, step = t.clientX - s.lx;
        s.lx = t.clientX;
        if (!s.lock && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) s.lock = Math.abs(dx) > Math.abs(dy) ? 1 : 2;
        if (s.lock !== 1 || !e.cancelable) return;
        // strip at an end and the finger moves outward: nothing to scroll, the browser would navigate back/forward
        var d = step || dx, x = w.scrollLeft;
        if ((d > 0 && x <= 0) || (d < 0 && x >= w.scrollWidth - w.clientWidth - 1)) e.preventDefault();
      },
      touchend: function (e) {
        if (!s.touching) return;
        s.touching = false;
        if (!PHONE.matches) { restart(s); return; }
        e.stopPropagation(); // Judge.me's flick handler would jump a card
        if (!HAS_SCROLLEND) later(s, 160);
        if (!s.moving) { follow(s); restart(s); } // a tap or an edge swipe: nothing to settle
      },
      wheel: function () { s.auto = false; },
      scroll: function () {
        if (!PHONE.matches) return;
        s.moving = true;
        if (!s.raf) s.raf = requestAnimationFrame(function () { s.raf = 0; paint(s); });
        later(s, HAS_SCROLLEND ? 600 : 160); // with scrollend: only a safety net
      },
      scrollend: function () { if (PHONE.matches) settle(s); },
      click: function (e) {
        var b = e.target.closest && e.target.closest('.ppf-rc-btn');
        if (!b || !PHONE.matches) return;
        var fn = b.getAttribute('data-dir') === '1' ? window.jdgmNextCard : window.jdgmPreviousCard;
        if (typeof fn !== 'function') return;
        s.byUser = true;
        fn(s.id);
        Promise.resolve().then(function () { s.byUser = false; }); // after the observer has seen the move
        restart(s);
      },
      focusin: function () {
        if (!PHONE.matches) return;
        s.focus = true;
        hold(s);
      },
      focusout: function (e) {
        if (e.relatedTarget && s.root.contains(e.relatedTarget)) return;
        s.focus = false;
        if (s.held) restart(s);
      }
    };
    w.addEventListener('touchstart', s.on.touchstart, { passive: true });
    w.addEventListener('touchmove', s.on.touchmove, { passive: false });
    w.addEventListener('touchend', s.on.touchend, { passive: true });
    w.addEventListener('touchcancel', s.on.touchend, { passive: true });
    w.addEventListener('wheel', s.on.wheel, { passive: true });
    w.addEventListener('scroll', s.on.scroll, { passive: true });
    if (HAS_SCROLLEND) w.addEventListener('scrollend', s.on.scrollend);
    s.ind.addEventListener('click', s.on.click);
    s.root.addEventListener('focusin', s.on.focusin);
    s.root.addEventListener('focusout', s.on.focusout);

    // Judge.me moved (autoplay, buttons) or rebuilt its cards
    s.mo = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        if (records[i].type === 'childList' && records[i].target === s.track) { rebuild(s); return; }
      }
      follow(s);
    });
    s.mo.observe(s.track, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });

    // Judge.me replaced its content block (or the wrapper inside it): start over on the new markup
    s.mo2 = new MutationObserver(function () { if (gone(s)) renew(s); });
    s.mo2.observe(s.root, { childList: true });
    s.mo2.observe(s.content, { childList: true });

    // new width or card size (rotation, late styles): new snap points; re-align unless the visitor is swiping
    if (typeof ResizeObserver === 'function') {
      s.ro = new ResizeObserver(function () {
        if (!PHONE.matches || !s.pos) return;
        if (w.clientWidth === s.lastW && s.bar.clientWidth === s.lastB && s.track.offsetWidth === s.lastT) return;
        measure(s);
        if (!s.touching && !s.moving) jump(s);
      });
      s.ro.observe(w);
      s.ro.observe(s.track);
    }
  }

  function gone(s) {
    return s.content.parentNode !== s.root || s.wrapper.parentNode !== s.content ||
      s.track.parentNode !== s.wrapper || s.ind.parentNode !== s.root;
  }

  function later(s, ms) {
    clearTimeout(s.timer);
    s.timer = setTimeout(function () { if (!s.touching && PHONE.matches) settle(s); }, ms);
  }

  function measure(s) {
    var w = s.wrapper;
    // page gutter: the strip bleeds to the screen edges and snaps the cards back onto the gutter
    var g = Math.max(0, Math.min(40, Math.round(s.content.getBoundingClientRect().left)));
    if (g !== s.g) { s.g = g; s.root.style.setProperty('--ppf-rc-g', g + 'px'); }
    var wr = w.getBoundingClientRect();
    var padL = parseFloat(getComputedStyle(w).paddingLeft) || 0;
    s.lastW = w.clientWidth;
    s.lastT = s.track.offsetWidth;
    s.max = Math.max(0, w.scrollWidth - w.clientWidth);
    s.pos = s.cards.map(function (c) {
      var x = c.getBoundingClientRect().left - wr.left + w.scrollLeft - padL;
      return Math.min(s.max, Math.max(0, Math.round(x)));
    });
    s.lastB = s.bar.clientWidth;
    s.thumbW = Math.min(s.lastB, Math.max(THUMB_MIN, s.lastB / s.n));
    s.thumb.style.width = s.thumbW + 'px';
    s.cur = -1;
  }

  function nearest(s, x) {
    var best = 0, bd = Infinity;
    for (var i = 0; i < s.n; i++) {
      var d = Math.abs(s.pos[i] - x);
      if (d < bd - 0.5) { bd = d; best = i; }
    }
    return best;
  }

  // position as a fraction between the snap points (the thumb follows the finger)
  function frac(s, x) {
    if (x <= s.pos[0]) return 0;
    for (var i = 0; i < s.n - 1; i++) {
      var a = s.pos[i], b = s.pos[i + 1];
      if (x <= b) return b > a ? i + (x - a) / (b - a) : i + 1;
    }
    return s.n - 1;
  }

  function paint(s) {
    if (!s.pos) return;
    var x = s.wrapper.scrollLeft;
    var f = frac(s, x) / (s.n - 1);
    s.thumb.style.transform = 'translateX(' + (f * (s.lastB - s.thumbW)).toFixed(1) + 'px)';
    var i = nearest(s, x);
    if (i === s.cur) return;
    s.cur = i;
    s.now.textContent = pad2(i + 1);
  }

  // screen readers: only the visitor's own moves are announced
  function announce(s, i) {
    var t = say(T.pos, i + 1, s.n);
    if (s.sr.textContent !== t) s.sr.textContent = t;
  }

  // a swipe (or keyboard/trackpad scroll) came to rest: Judge.me follows the visitor
  function settle(s) {
    clearTimeout(s.timer);
    s.moving = false;
    if (!s.pos || gone(s)) return;
    var i = nearest(s, s.wrapper.scrollLeft);
    paint(s);
    if (s.auto) {
      s.auto = false;
      unclone(s);
    } else if (i !== s.goal || s.state.index !== i) {
      s.goal = i;
      commit(s, i);
      announce(s, i);
      restart(s);
      return;
    }
    if (s.held) restart(s);
  }

  function commit(s, i) {
    s.state.index = i;
    var kids = s.track.children;
    for (var k = 0; k < kids.length; k++) {
      var on = kids[k] === s.cards[i];
      if (kids[k].classList.contains('active') !== on) kids[k].classList.toggle('active', on);
    }
  }

  // Judge.me's loop step (last -> first) leaves .active on a hidden clone until a transform transition ends,
  // which never happens on the strip: put it on the real card
  function unclone(s) {
    var a = s.track.querySelector('.jdgm-media-card.active');
    if (a && a.hasAttribute('data-clone-type') && typeof s.state.index === 'number') commit(s, clamp(s, s.state.index));
  }

  // Judge.me's autoplay timer: held while the visitor touches the strip or has keyboard focus in the widget
  function hold(s) {
    var inst = s.root._videoCarouselInstance;
    if (inst && typeof inst.stopTimer === 'function') { inst.stopTimer(); s.held = true; }
  }

  function restart(s) {
    if (s.touching || s.focus) return;
    s.held = false;
    var inst = s.root._videoCarouselInstance;
    if (inst && typeof inst.resetTimer === 'function' && s.root._isInViewport) inst.resetTimer();
  }

  function follow(s) {
    if (!PHONE.matches || s.touching || !s.pos) return;
    if (s.moving && !s.auto) return; // the visitor's swipe is still running: settle() decides
    if (gone(s)) return;
    var a = s.track.querySelector('.jdgm-media-card.active');
    if (!a || a.parentNode !== s.track) return;
    var i = parseInt(a.getAttribute('data-card-index'), 10);
    if (isNaN(i) || i < 0 || i >= s.n) return;
    var user = s.byUser;
    s.byUser = false;
    if (i === s.goal) { unclone(s); return; }
    // no autoplay for reduced motion or while keyboard focus is inside (in case Judge.me restarted its timer)
    if ((REDUCE.matches || s.focus) && !user) { commit(s, s.goal); return; }
    if (user) announce(s, i);
    go(s, i, true);
    unclone(s);
  }

  function go(s, i, smooth) {
    s.goal = i;
    var x = s.pos[i];
    if (Math.abs(x - s.wrapper.scrollLeft) < 1) { paint(s); return; }
    s.auto = true;
    s.wrapper.scrollTo({ left: x, behavior: smooth && !REDUCE.matches ? 'smooth' : 'auto' });
  }

  function jump(s) {
    go(s, clamp(s, s.state.index), false);
    paint(s);
  }

  function mode(s) {
    s.moving = false;
    if (PHONE.matches) {
      s.wasPhone = true;
      measure(s);
      jump(s);
    } else {
      s.focus = false;
      if (s.held) restart(s);
      if (s.wasPhone) {
        s.wasPhone = false;
        s.wrapper.scrollLeft = 0;
        realign(s);
      }
    }
  }

  // back on Judge.me's transform layout: its last transform was computed against the phone strip,
  // so put its active card where its own navigation would have put it
  function realign(s) {
    var c = window.jdgmCarouselCenter;
    if (s.root._initialPositioningPending || !c || typeof c.calculateAndApplyTransform !== 'function') return;
    unclone(s);
    var a = s.track.querySelector('.jdgm-media-card.active');
    if (!a) return;
    var t = s.track.style.transition;
    s.track.style.transition = 'none';
    c.calculateAndApplyTransform(s.track, a, s.wrapper, function (r) { return r.left; }, function (r) { return r.left; });
    void s.track.offsetHeight;
    s.track.style.transition = t;
  }

  function rebuild(s) {
    var p = read(s.root);
    if (!p || p.cards.length !== s.n) { teardown(s); return; }
    s.cards = p.cards;
    if (PHONE.matches) { measure(s); jump(s); }
  }

  function teardown(s) {
    var w = s.wrapper;
    if (s.on) {
      w.removeEventListener('touchstart', s.on.touchstart);
      w.removeEventListener('touchmove', s.on.touchmove);
      w.removeEventListener('touchend', s.on.touchend);
      w.removeEventListener('touchcancel', s.on.touchend);
      w.removeEventListener('wheel', s.on.wheel);
      w.removeEventListener('scroll', s.on.scroll);
      w.removeEventListener('scrollend', s.on.scrollend);
      s.root.removeEventListener('focusin', s.on.focusin);
      s.root.removeEventListener('focusout', s.on.focusout);
    }
    if (s.mo) s.mo.disconnect();
    if (s.mo2) s.mo2.disconnect();
    if (s.ro) s.ro.disconnect();
    clearTimeout(s.timer);
    if (s.ind && s.ind.parentNode) s.ind.parentNode.removeChild(s.ind);
    s.root.classList.remove(ON);
    s.root.style.removeProperty('--ppf-rc-g');
    var at = list.indexOf(s);
    if (at > -1) list.splice(at, 1);
    s.touching = false;
    s.focus = false;
    if (s.held) restart(s);
    if (s.wasPhone) {
      s.wasPhone = false;
      w.scrollLeft = 0;
      if (w.isConnected) realign(s);
    }
  }

  function renew(s) {
    var root = s.root;
    teardown(s);
    root.__ppfRcN = (root.__ppfRcN || 0) + 1;
    if (root.__ppfRcN > MAX_RENEW) return; // stay on Judge.me's own carousel
    root.__ppfRc = null;
    watch(root);
  }

  function onPhone() {
    list.slice().forEach(function (s) {
      try { mode(s); } catch (err) { teardown(s); }
    });
  }
  if (PHONE.addEventListener) PHONE.addEventListener('change', onPhone);
  else if (PHONE.addListener) PHONE.addListener(onPhone);

  // Judge.me renders late (fetch): watch each widget until its cards are there
  function watch(root) {
    if (tryInit(root)) return;
    var ro = null;
    var check = function () {
      if (!tryInit(root)) return;
      mo.disconnect();
      if (ro) ro.disconnect();
    };
    var mo = new MutationObserver(check);
    mo.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    if (typeof ResizeObserver === 'function') { ro = new ResizeObserver(check); ro.observe(root); } // late app styles
    setTimeout(function () { mo.disconnect(); if (ro) ro.disconnect(); }, 60000);
  }

  function start() {
    var roots = document.querySelectorAll('.jdgm-cards-carousel');
    if (!roots.length) return;
    var css = document.getElementById('ppf-rc-css');
    var run = function () { Array.prototype.forEach.call(roots, watch); };
    // the stylesheet is parser-inserted before this deferred script, so it has normally loaded (or failed) by now
    if (cssReady(roots[0]) || !css) run();
    else css.addEventListener('load', run, { once: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
